import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  addDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  collectionGroup,
  limit as firestoreLimit,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useUser } from './UserContext';
import { Organization, OrganizationFormData, OrganizationMember, OrganizationRole, OrganizationMemberStatus } from '../types/Organization';
import { v4 as uuidv4 } from 'uuid';
import crypto from 'crypto';

// Define the context type
interface OrganizationContextType {
  organization: Organization | null;
  currentOrganization: Organization | null;
  setCurrentOrganization: React.Dispatch<React.SetStateAction<Organization | null>>;
  loadingOrganization: boolean;
  organizationError: string | null;
  members: OrganizationMember[];
  loadingMembers: boolean;
  inviteUser: (email: string, role: string) => Promise<string>;
  userOrganizations: Organization[];
  loadingUserOrganizations: boolean;
  switchToPersonalAccount: () => void;
  isPersonalMode: boolean;
  switchToOrganization: (orgId: string | null) => Promise<void>;
  
  // New member management functions
  inviteMember: (organizationId: string, email: string, role?: OrganizationRole, bypassVerification?: boolean) => Promise<string>;
  removeMember: (organizationId: string, memberId: string) => Promise<void>;
  updateMemberRole: (organizationId: string, memberId: string, newRole: OrganizationRole) => Promise<void>;
  acceptInvitation: (token: string) => Promise<void>;
  declineInvitation: (token: string) => Promise<void>;
  getMemberPermissions: (organizationId: string, userId: string) => Promise<OrganizationMember['permissions'] | null>;
  canCurrentUserManageMembers: () => boolean;
  logMemberActivity: (organizationId: string, action: any, details?: any) => Promise<void>;
  getMemberActivities: (organizationId: string, memberId?: string, limit?: number) => Promise<any[]>;
  
  // For backward compatibility - to be removed in next update
  activeOrganization?: Organization | null;
  organizations?: Organization[];
  isPersonalAccount?: boolean;
  isOrgAdmin?: boolean;
  error?: string | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

const generateId = (): string => {
  return uuidv4();
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useUser();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(null);
  const [isPersonalAccount, setIsPersonalAccount] = useState<boolean>(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [memberActivities, setMemberActivities] = useState<any[]>([]);

  // Load user's organizations
  useEffect(() => {
    if (!currentUser) {
      setOrganizations([]);
      setActiveOrganization(null);
      setIsPersonalAccount(true);
      setIsLoadingOrgs(false);
      return;
    }

    setIsLoadingOrgs(true);
    setError(null);

    const fetchOrganizations = async () => {
      try {
        // First, get organizations where the user is the creator
        const ownedOrgsQuery = query(
          collection(db, 'organizations'),
          where('creatorId', '==', currentUser.uid)
        );
        
        // Then, get organizations where the user is a member
        const memberOrgsQuery = query(
          collection(db, 'organizations'),
          where('members', 'array-contains', { userId: currentUser.uid })
        );
        
        const [ownedSnapshot, memberSnapshot] = await Promise.all([
          getDocs(ownedOrgsQuery),
          getDocs(memberOrgsQuery)
        ]);
        
        const ownedOrgs: Organization[] = [];
        ownedSnapshot.forEach(doc => {
          ownedOrgs.push({ id: doc.id, ...doc.data() } as Organization);
        });
        
        const memberOrgs: Organization[] = [];
        memberSnapshot.forEach(doc => {
          // Avoid duplicates (if user is both creator and listed in members)
          if (!ownedOrgs.some(org => org.id === doc.id)) {
            memberOrgs.push({ id: doc.id, ...doc.data() } as Organization);
          }
        });
        
        const allOrgs = [...ownedOrgs, ...memberOrgs];
        setOrganizations(allOrgs);
        
        // Check if we need to restore an active organization from localStorage
        const storedOrgId = localStorage.getItem('activeOrganizationId');
        if (storedOrgId) {
          const storedOrg = allOrgs.find(org => org.id === storedOrgId);
          if (storedOrg) {
            setActiveOrganization(storedOrg);
            setIsPersonalAccount(false);
          } else {
            // Clear invalid stored organization
            localStorage.removeItem('activeOrganizationId');
            setIsPersonalAccount(true);
          }
        } else {
          setIsPersonalAccount(true);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError('Failed to load your organizations');
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, [currentUser]);

  // Function to get organization members
  const fetchOrganizationMembers = async (organizationId: string) => {
    if (!organizationId) return;
    
    try {
      setLoadingMembers(true);

      
      const membersRef = collection(db, 'organizations', organizationId, 'members');
      const membersSnap = await getDocs(membersRef);

      const fetchedMembers: OrganizationMember[] = [];

      membersSnap.forEach((doc) => {
         fetchedMembers.push({
            id: doc.id,
            ...doc.data(),
         } as OrganizationMember);
      });
      

      setMembers(fetchedMembers);
      return fetchedMembers;
    } catch (error) {
      console.error('Error fetching organization members:', error);
      setError('Failed to load organization members');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Function to invite a member
  const inviteMember = async (
     organizationId: string,
     email: string,
     role: OrganizationRole = 'member',
     bypassVerification: boolean = false
  ): Promise<string> => {
     if (!currentUser || !organizationId) {
        throw new Error('Not authorized to invite members');
     }

     try {
        // Generate unique invitation ID and token
        const invitationId = generateId();
        const token = uuidv4();

        // Set up default permissions
        const permissions: OrganizationMember['permissions'] = {
           canManageEvents: role === 'owner' || role === 'admin',
           canManageContacts: role === 'owner' || role === 'admin',
           canManageAssets: role === 'owner' || role === 'admin',
           canManageSettings: role === 'owner' || role === 'admin',
           canManageMembers: role === 'owner' || role === 'admin',
           canViewMembers: role === 'owner' || role === 'admin',
           canRemoveOwner: role === 'owner',
           canViewAnalytics: role === 'owner' || role === 'admin',
           canViewEvents: true,
           canCreateEvents: true,
           canEditEvents: role === 'owner' || role === 'admin',
           canDeleteEvents: role === 'owner' || role === 'admin',
           canViewTrips: true,
           canCreateTrips: true,
           canEditTrips: role === 'owner' || role === 'admin',
           canDeleteTrips: role === 'owner' || role === 'admin',
           canSendMessages: true,
        };

        // Create member document with pending status
        const memberData: Partial<OrganizationMember> = {
           email,
           role,
           status: bypassVerification ? 'active' : 'pending',
           invitedBy: currentUser.displayName || currentUser.email || 'Unknown User',
           invitedAt: serverTimestamp() as any,
           invitationToken: token,
           permissions,
        };

        if (bypassVerification) {
           // For testing purposes, create a mock user ID if bypassing verification
           memberData.userId = `test-user-${Date.now()}`;
           memberData.name = email.split('@')[0]; // Use part before @ as name
           memberData.displayName = email.split('@')[0];
        }

        // Add to members collection
        const memberRef = doc(
           collection(db, 'organizations', organizationId, 'members'),
           invitationId
        );
        await setDoc(memberRef, memberData);

        // Log the activity
        await logMemberActivity(organizationId, 'member_invited', {
           email,
           role,
           bypassVerification,
           invitedBy: currentUser.displayName || currentUser.email,
        });

        // In a real app, you would send an email with the invitation token

        if (bypassVerification) {
        }

        // Refresh the members list immediately after inviting
        const updatedMembers = await fetchOrganizationMembers(organizationId);

        // Force a state update by creating a new array
        if (updatedMembers) {
           setMembers([...updatedMembers]);
        }

        return invitationId;
     } catch (err) {
        console.error('Error inviting member:', err);
        throw err; // Let the original error propagate to help with debugging
     }
  };

  // Function to remove a member
  const removeMember = async (organizationId: string, memberId: string): Promise<void> => {
     if (!currentUser || !organizationId) {
        throw new Error('Not authorized to remove members');
     }

     try {
        // Get the organization
        const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
        if (!orgDoc.exists()) {
           throw new Error('Organization not found');
        }

        const organization = { id: orgDoc.id, ...orgDoc.data() } as Organization;

        // Get the member to remove
        const memberToRemoveDoc = await getDoc(
           doc(db, 'organizations', organizationId, 'members', memberId)
        );
        if (!memberToRemoveDoc.exists()) {
           throw new Error('Member not found');
        }

        const memberToRemove = {
           id: memberToRemoveDoc.id,
           ...memberToRemoveDoc.data(),
        } as OrganizationMember;

        // Check permissions - only owners can remove other owners
        const currentMember = await getMemberByUserId(organizationId, currentUser.uid);
        if (!currentMember) {
           throw new Error('You are not a member of this organization');
        }

        // If trying to remove the owner and current user is not the owner
        if (memberToRemove.role === 'owner' && currentMember.role !== 'owner') {
           throw new Error('Only the organization owner can remove an owner');
        }

        // If the member being removed is the only owner, prevent it
        if (memberToRemove.role === 'owner') {
           const ownersQuery = query(
              collection(db, 'organizations', organizationId, 'members'),
              where('role', '==', 'owner')
           );
           const ownersSnapshot = await getDocs(ownersQuery);
           if (ownersSnapshot.size === 1 && ownersSnapshot.docs[0].id === memberId) {
              throw new Error('Cannot remove the only owner of the organization');
           }
        }

        // Delete the member document
        await deleteDoc(doc(db, 'organizations', organizationId, 'members', memberId));

        // Log the activity
        await logMemberActivity(organizationId, 'member_removed', {
           memberId,
           email: memberToRemove.email,
           name: memberToRemove.name || memberToRemove.displayName,
           removedBy: currentUser.displayName || currentUser.email || 'Unknown User',
        });

        // Refresh the members list
        const updatedMembers = await fetchOrganizationMembers(organizationId);

        // Force a state update
        if (updatedMembers) {
           setMembers([...updatedMembers]);
        }
     } catch (err) {
        console.error('Error removing member:', err);
        throw err;
     }
  };

  // Function to update a member's role
  const updateMemberRole = async (
     organizationId: string,
     memberId: string,
     newRole: OrganizationRole
  ): Promise<void> => {
     if (!currentUser || !organizationId) {
        throw new Error('Not authorized to update member roles');
     }

     try {
        // Check permissions - only owners can change other owners
        const currentMember = await getMemberByUserId(organizationId, currentUser.uid);
        if (!currentMember) {
           throw new Error('You are not a member of this organization');
        }

        // Get the member to update
        const memberToUpdateDoc = await getDoc(
           doc(db, 'organizations', organizationId, 'members', memberId)
        );
        if (!memberToUpdateDoc.exists()) {
           throw new Error('Member not found');
        }

        const memberToUpdate = {
           id: memberToUpdateDoc.id,
           ...memberToUpdateDoc.data(),
        } as OrganizationMember;

        // If trying to modify an owner and current user is not the owner
        if (memberToUpdate.role === 'owner' && currentMember.role !== 'owner') {
           throw new Error("Only the organization owner can modify an owner's role");
        }

        // If trying to assign owner role and current user is not the owner
        if (newRole === 'owner' && currentMember.role !== 'owner') {
           throw new Error('Only the organization owner can assign the owner role');
        }

        // Get the organization to update its members array
        const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
        if (!orgDoc.exists()) {
           throw new Error('Organization not found');
        }

        const organization = { id: orgDoc.id, ...orgDoc.data() } as Organization;

        // If downgrading from owner to another role, ensure there's at least one other owner
        if (memberToUpdate.role === 'owner' && newRole !== 'owner') {
           const ownersQuery = query(
              collection(db, 'organizations', organizationId, 'members'),
              where('role', '==', 'owner')
           );
           const ownersSnapshot = await getDocs(ownersQuery);
           if (ownersSnapshot.size === 1 && ownersSnapshot.docs[0].id === memberId) {
              throw new Error('Cannot downgrade the only owner of the organization');
           }
        }

        // Set permissions based on role
        const permissions: OrganizationMember['permissions'] = {
           canManageEvents: newRole === 'owner' || newRole === 'admin',
           canManageContacts: newRole === 'owner' || newRole === 'admin',
           canManageAssets: newRole === 'owner' || newRole === 'admin',
           canManageSettings: newRole === 'owner' || newRole === 'admin',
           canManageMembers: newRole === 'owner' || newRole === 'admin',
           canRemoveOwner: newRole === 'owner', // Only owners can remove other owners
           canViewAnalytics: true,
           canSendMessages: true,
           canViewMembers: newRole === 'owner' || newRole === 'admin',
           canViewEvents: true,
           canCreateEvents: true,
           canEditEvents: newRole === 'owner' || newRole === 'admin',
           canDeleteEvents: newRole === 'owner' || newRole === 'admin',
           canViewTrips: true,
           canCreateTrips: true,
           canEditTrips: newRole === 'owner' || newRole === 'admin',
           canDeleteTrips: newRole === 'owner' || newRole === 'admin',
        };

        // Update member document
        await updateDoc(doc(db, 'organizations', organizationId, 'members', memberId), {
           role: newRole,
           permissions,
           updatedAt: serverTimestamp(),
        });

        // Update the member in the organization's members array
        const updatedMembers = organization.members.map((m) => {
           if (m.id === memberId) {
              return { ...m, role: newRole, permissions };
           }
           return m;
        });

        await updateDoc(doc(db, 'organizations', organizationId), {
           members: updatedMembers,
           updatedAt: serverTimestamp(),
        });

        // Log the activity
        await logMemberActivity(organizationId, 'member_role_changed', {
           memberId,
           oldRole: memberToUpdate.role,
           newRole,
        });

        // Refresh members list if needed
        if (activeOrganization?.id === organizationId) {
           await fetchOrganizationMembers(organizationId);
        }
     } catch (err) {
        console.error('Error updating member role:', err);
        throw new Error('Failed to update member role. Please try again.');
     }
  };

  // Handle member invitation acceptance
  const acceptInvitation = async (token: string): Promise<void> => {
     if (!currentUser) {
        throw new Error('You must be logged in to accept an invitation');
     }

     try {
        // Find the invitation by token
        const invitationsQuery = query(
           collectionGroup(db, 'invitations'),
           where('token', '==', token),
           where('status', '==', 'pending')
        );
        const invitationsSnapshot = await getDocs(invitationsQuery);

        if (invitationsSnapshot.empty) {
           throw new Error('Invalid or expired invitation');
        }

        const invitationDoc = invitationsSnapshot.docs[0];
        const invitation = { id: invitationDoc.id, ...invitationDoc.data() } as any;

        // Check if invitation has expired
        const expiresAt = new Date(invitation.expiresAt);
        if (expiresAt < new Date()) {
           throw new Error('This invitation has expired');
        }

        // Check if user email matches invitation email
        if (currentUser.email && currentUser.email !== invitation.email) {
           throw new Error('This invitation was sent to a different email address');
        }

        // Create a new member entry
        const newMember: OrganizationMember = {
           id: generateId(),
           userId: currentUser.uid,
           organizationId: invitation.organizationId,
           email: currentUser.email || invitation.email,
           name: currentUser.displayName || invitation.email.split('@')[0],
           displayName: currentUser.displayName || invitation.email.split('@')[0],
           role: invitation.role,
           status: 'active',
           joinedAt: new Date(),
           invitedBy: invitation.invitedBy,
           invitedAt: invitation.invitedAt,
           avatarUrl: currentUser.photoURL || '',
           photoURL: currentUser.photoURL || '',
           permissions: {
              canManageEvents: invitation.role === 'owner' || invitation.role === 'admin',
              canManageContacts: invitation.role === 'owner' || invitation.role === 'admin',
              canManageAssets: invitation.role === 'owner' || invitation.role === 'admin',
              canManageSettings: invitation.role === 'owner' || invitation.role === 'admin',
              canManageMembers: invitation.role === 'owner' || invitation.role === 'admin',
              canRemoveOwner: invitation.role === 'owner', // Only owners can remove other owners
              canViewAnalytics: true,
              canSendMessages: true,
              canViewMembers: invitation.role === 'owner' || invitation.role === 'admin',
              canViewEvents: true,
              canCreateEvents: true,
              canEditEvents: invitation.role === 'owner' || invitation.role === 'admin',
              canDeleteEvents: invitation.role === 'owner' || invitation.role === 'admin',
              canViewTrips: true,
              canCreateTrips: true,
              canEditTrips: invitation.role === 'owner' || invitation.role === 'admin',
              canDeleteTrips: invitation.role === 'owner' || invitation.role === 'admin',
           },
        };

        // Add to members collection
        await setDoc(
           doc(db, 'organizations', invitation.organizationId, 'members', newMember.id),
           newMember
        );

        // Update the organization document to include the new member
        const orgDoc = await getDoc(doc(db, 'organizations', invitation.organizationId));
        if (orgDoc.exists()) {
           const orgData = orgDoc.data();
           const updatedMembers = [...(orgData.members || []), newMember];

           await updateDoc(doc(db, 'organizations', invitation.organizationId), {
              members: updatedMembers,
              updatedAt: serverTimestamp(),
           });
        }

        // Update invitation status
        await updateDoc(
           doc(db, 'organizations', invitation.organizationId, 'invitations', invitation.id),
           {
              status: 'accepted',
              acceptedAt: new Date(),
           }
        );

        // Log activity
        await logMemberActivity(invitation.organizationId, 'invite_accepted', {
           invitationId: invitation.id,
        });

        // Refresh organization data if needed
        await fetchUserOrganizations();
     } catch (err) {
        console.error('Error accepting invitation:', err);
        throw new Error('Failed to accept invitation. Please try again.');
     }
  };

  // Handle member invitation decline
  const declineInvitation = async (token: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('You must be logged in to decline an invitation');
    }

    try {
      // Find the invitation by token
      const invitationsQuery = query(
        collectionGroup(db, 'invitations'),
        where('token', '==', token),
        where('status', '==', 'pending')
      );
      const invitationsSnapshot = await getDocs(invitationsQuery);

      if (invitationsSnapshot.empty) {
        throw new Error('Invalid or expired invitation');
      }

      const invitationDoc = invitationsSnapshot.docs[0];
      const invitation = { id: invitationDoc.id, ...invitationDoc.data() } as any;

      // Update invitation status
      await updateDoc(doc(db, 'organizations', invitation.organizationId, 'invitations', invitation.id), {
        status: 'declined',
        declinedAt: new Date()
      });
    } catch (err) {
      console.error('Error declining invitation:', err);
      throw new Error('Failed to decline invitation. Please try again.');
    }
  };

  // Helper function to get a member by user ID
  const getMemberByUserId = async (organizationId: string, userId: string): Promise<OrganizationMember | null> => {
    try {
      const membersQuery = query(
        collection(db, 'organizations', organizationId, 'members'),
        where('userId', '==', userId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      if (membersSnapshot.empty) {
        return null;
      }

      const memberDoc = membersSnapshot.docs[0];
      return { id: memberDoc.id, ...memberDoc.data() } as OrganizationMember;
    } catch (err) {
      console.error('Error getting member by user ID:', err);
      return null;
    }
  };

  // Helper function to get default permissions based on role
  const getDefaultPermissions = (role: OrganizationRole): OrganizationMember['permissions'] => {
    switch (role) {
      case 'owner':
        return {
          canManageEvents: true,
          canManageContacts: true,
          canManageAssets: true,
          canManageSettings: true,
          canManageMembers: true,
          canRemoveOwner: true,
          canViewAnalytics: true,
          canSendMessages: true,
          canViewMembers: true,
          canViewEvents: true,
          canCreateEvents: true,
          canEditEvents: true,
          canDeleteEvents: true,
          canViewTrips: true,
          canCreateTrips: true,
          canEditTrips: true,
          canDeleteTrips: true
        };
      case 'admin':
        return {
          canManageEvents: true,
          canManageContacts: true,
          canManageAssets: true,
          canManageSettings: true,
          canManageMembers: true,
          canRemoveOwner: false,
          canViewAnalytics: true,
          canSendMessages: true,
          canViewMembers: true,
          canViewEvents: true,
          canCreateEvents: true,
          canEditEvents: true,
          canDeleteEvents: true,
          canViewTrips: true,
          canCreateTrips: true,
          canEditTrips: true,
          canDeleteTrips: true
        };
      case 'member':
        return {
          canManageEvents: true,
          canManageContacts: true,
          canManageAssets: true,
          canManageSettings: false,
          canManageMembers: false,
          canRemoveOwner: false,
          canViewAnalytics: true,
          canSendMessages: true,
          canViewMembers: false,
          canViewEvents: true,
          canCreateEvents: true,
          canEditEvents: false,
          canDeleteEvents: false,
          canViewTrips: true,
          canCreateTrips: true,
          canEditTrips: false,
          canDeleteTrips: false
        };
      default:
        return {
          canManageEvents: false,
          canManageContacts: false,
          canManageAssets: false,
          canManageSettings: false,
          canManageMembers: false,
          canRemoveOwner: false,
          canViewAnalytics: false,
          canSendMessages: true,
          canViewMembers: false,
          canViewEvents: false,
          canCreateEvents: false,
          canEditEvents: false,
          canDeleteEvents: false,
          canViewTrips: false,
          canCreateTrips: false,
          canEditTrips: false,
          canDeleteTrips: false
        };
    }
  };

  // Get member permissions
  const getMemberPermissions = async (
    organizationId: string, 
    userId: string
  ): Promise<OrganizationMember['permissions'] | null> => {
    const member = await getMemberByUserId(organizationId, userId);
    return member?.permissions || null;
  };

  // Check if current user can manage members
  const canCurrentUserManageMembers = (): boolean => {
    if (!currentUser || !activeOrganization) return false;

    const currentMember = activeOrganization.members.find(
      member => member.userId === currentUser.uid
    );

    if (!currentMember) return false;

    return currentMember.role === 'owner' || 
           currentMember.role === 'admin' || 
           (currentMember.permissions?.canManageMembers || false);
  };

  // Log member activity
  const logMemberActivity = async (
    organizationId: string, 
    action: any, 
    details?: any
  ): Promise<void> => {
    if (!currentUser || !organizationId) return;

    try {
      const currentMember = await getMemberByUserId(organizationId, currentUser.uid);
      if (!currentMember) return;

      const activityData: any = {
        id: generateId(),
        organizationId,
        memberId: currentMember.id,
        userId: currentUser.uid,
        action,
        timestamp: new Date(),
        details,
        ipAddress: '',  // Would be set server-side in a real implementation
        userAgent: navigator.userAgent
      };

      await setDoc(
        doc(db, 'organizations', organizationId, 'activityLogs', activityData.id),
        activityData
      );
    } catch (err) {
      console.error('Error logging member activity:', err);
      // Non-critical error, just log it
    }
  };

  // Get member activities
  const getMemberActivities = async (
    organizationId: string, 
    memberId?: string, 
    limit: number = 20
  ): Promise<any[]> => {
    if (!organizationId) return [];

    try {
      let activitiesQuery;
      if (memberId) {
        activitiesQuery = query(
          collection(db, 'organizations', organizationId, 'activityLogs'),
          where('memberId', '==', memberId),
          orderBy('timestamp', 'desc'),
          firestoreLimit(limit)
        );
      } else {
        activitiesQuery = query(
          collection(db, 'organizations', organizationId, 'activityLogs'),
          orderBy('timestamp', 'desc'),
          firestoreLimit(limit)
        );
      }

      const activitiesSnapshot = await getDocs(activitiesQuery);
      const activities: any[] = [];

      activitiesSnapshot.forEach(doc => {
        activities.push({ id: doc.id, ...doc.data() });
      });

      return activities;
    } catch (err) {
      console.error('Error getting member activities:', err);
      return [];
    }
  };

  // Switch to personal account
  const switchToPersonalAccount = () => {
    setIsPersonalAccount(true);
    setActiveOrganization(null);
    localStorage.removeItem('activeOrganizationId');
  };

  // Fetch user organizations
  const fetchUserOrganizations = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoadingOrgs(true);
      // First, get organizations where the user is the creator
      const ownedOrgsQuery = query(
        collection(db, 'organizations'),
        where('creatorId', '==', currentUser.uid)
      );
      
      // Then, get organizations where the user is a member
      const memberOrgsQuery = query(
        collection(db, 'organizations'),
        where('members', 'array-contains', { userId: currentUser.uid })
      );
      
      const [ownedSnapshot, memberSnapshot] = await Promise.all([
        getDocs(ownedOrgsQuery),
        getDocs(memberOrgsQuery)
      ]);
      
      const ownedOrgs: Organization[] = [];
      ownedSnapshot.forEach(doc => {
        ownedOrgs.push({ id: doc.id, ...doc.data() } as Organization);
      });
      
      const memberOrgs: Organization[] = [];
      memberSnapshot.forEach(doc => {
        // Avoid duplicates (if user is both creator and listed in members)
        if (!ownedOrgs.some(org => org.id === doc.id)) {
          memberOrgs.push({ id: doc.id, ...doc.data() } as Organization);
        }
      });
      
      const allOrgs = [...ownedOrgs, ...memberOrgs];
      setOrganizations(allOrgs);
      
      setIsLoadingOrgs(false);
    } catch (err) {
      console.error('Error fetching user organizations:', err);
      setError('Failed to load organizations');
      setIsLoadingOrgs(false);
    }
  };

  // Context value object
  const value: OrganizationContextType = {
    organization: activeOrganization,
    currentOrganization: activeOrganization,
    setCurrentOrganization: setActiveOrganization,
    loadingOrganization: isLoadingOrgs,
    organizationError: error,
    members,
    loadingMembers,
    inviteUser: async (email, role) => await inviteMember(activeOrganization?.id || '', email, role as OrganizationRole),
    userOrganizations: organizations,
    loadingUserOrganizations: isLoadingOrgs,
    switchToPersonalAccount,
    isPersonalMode: isPersonalAccount,
    switchToOrganization: async (orgId) => {
      if (orgId) {
        const organization = organizations.find((org) => org.id === orgId);
        if (organization) {
          setActiveOrganization(organization);
          localStorage.setItem('activeOrganizationId', orgId);
          setIsPersonalAccount(false);
          
          // Fetch members when switching to an organization
          await fetchOrganizationMembers(orgId);
        }
      } else {
        setActiveOrganization(null);
        localStorage.removeItem('activeOrganizationId');
        setIsPersonalAccount(true);
      }
    },
    
    // New member management functions
    inviteMember,
    removeMember,
    updateMemberRole,
    acceptInvitation,
    declineInvitation,
    getMemberPermissions,
    canCurrentUserManageMembers,
    logMemberActivity,
    getMemberActivities,
    
    // For backward compatibility
    activeOrganization,
    organizations,
    isPersonalAccount,
    isOrgAdmin: activeOrganization ? activeOrganization.members.some(m => 
      m.userId === currentUser?.uid && (m.role === 'owner' || m.role === 'admin')
    ) : false,
    error
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}; 